import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function DinnerDecisionAppPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Dinner Decision App — End the 'Where Should We Eat?' Argument | Hangrily"
        description="The #1 dinner decision app for couples, families & friend groups. Hangrily uses Tinder-style swiping to find a restaurant everyone agrees on in under 2 minutes. Real photos, ratings, location-based. Works solo too. Free to try, no account needed."
        path="/dinner-decision-app"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Dinner Decision App - Hangrily',
            description: 'The dinner decision app that ends restaurant arguments. Swipe through nearby restaurants and match with your group.',
            url: 'https://hangrily.app/dinner-decision-app'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Dinner Decision App', item: 'https://hangrily.app/dinner-decision-app' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is the best dinner decision app?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is the #1 dinner decision app. It uses Tinder-style swiping through real nearby restaurants with photos, ratings, and prices. Solo, Couple, and Group modes let you decide alone or with up to 10 people. Most sessions take under 2 minutes.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is there an app that decides dinner for you?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Hangrily decides dinner by showing you restaurant cards one at a time. Swipe right to like, left to pass. After up to 3 rounds, it picks the restaurant with the highest score. For couples and groups, it finds the restaurant everyone agreed on.'
                }
              },
              {
                '@type': 'Question',
                name: 'How do couples decide where to eat for dinner?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'With Hangrily\'s Couple Mode, both partners swipe through the same restaurants independently. Neither sees the other\'s choices. After both finish, the app reveals mutual matches — restaurants you both liked. It completely eliminates the "I don\'t care, you pick" cycle.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can a family use a dinner decision app?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely. Hangrily\'s Group Mode supports up to 10 people — perfect for families. Everyone joins with a 6-letter code, swipes on their phone, and the app finds a restaurant the whole family agrees on. Kids love the swipe interface too.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — try free with no account needed. Guest users get 3 free sessions per week. Premium is $4.99/month for unlimited sessions, decision history, analytics, favorites, and rewards.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>The Dinner Decision App</h1>
          <p className="hero-subtitle">
            End the nightly "where should we eat?" argument. Hangrily decides dinner in 2 minutes.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>The App That Ends Dinner Arguments</h2>
              <p>
                Every evening, millions of couples and families face the same exhausting question: 
                <strong> "Where should we eat for dinner?"</strong> One person wants Italian, the other wants 
                Thai. Someone suggests pizza — vetoed. Twenty minutes later, you're hangry and ordering from 
                the same boring delivery place.
              </p>
              <p>
                Hangrily is the dinner decision app that actually works. Set your preferences, swipe through 
                nearby restaurants with real photos and ratings, and let our algorithm find the perfect match. 
                For couples: both swipe independently and only match on mutual favorites. For groups: up to 10 
                people vote democratically. <strong>Decision made in under 2 minutes. Zero arguments.</strong>
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Decide Dinner Now — Free →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">30 min</span>
                  <span className="stat-label-sm">Average time wasted deciding dinner</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">With Hangrily</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">94%</span>
                  <span className="stat-label-sm">Happy with their dinner match</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">0</span>
                  <span className="stat-label-sm">Dinner arguments</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Why Dinner Decisions Are So Hard</h2>
          <div className="reason-cards">
            <div className="reason-card">
              <div className="reason-icon">😴</div>
              <h3>End-of-Day Brain Fog</h3>
              <p>After a full day of work, your brain has made 35,000+ decisions. By 6 PM, choosing dinner feels impossible. Hangrily reduces it to simple swipe-right, swipe-left decisions.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">🔄</div>
              <h3>The Veto Cycle</h3>
              <p>"How about Thai?" "No." "Italian?" "Had it yesterday." "Burgers?" "Too heavy." This loop can last 30+ minutes. Hangrily's matching eliminates vetoes — you only see restaurants everyone liked.</p>
            </div>
            <div className="reason-card">
              <div className="reason-icon">📱</div>
              <h3>Too Many Options</h3>
              <p>Opening Google Maps and seeing 50 restaurants nearby is paralyzing. Hangrily shows you one restaurant at a time, pre-filtered by your preferences. Simple, fast, effective.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">How the Dinner Decision App Works</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Set Your Dinner Preferences</h3>
                <p>Choose from 12+ cuisine types, set your price range, pick your mood (comfort food, adventurous, healthy, etc.), and set your distance limit. Hangrily uses GPS to find real restaurants near you.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Everyone Swipes</h3>
                <p>See restaurant cards with real photos, Google ratings, price level, and distance. Swipe right to like, left to pass, up to love. If deciding with others, everyone swipes on their own device.</p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>Dinner Is Decided</h3>
                <p>After up to 3 rounds, our algorithm reveals your top restaurant match plus runner-ups. Get directions, read details, and head out. The whole process takes about 2 minutes.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Every Dinner Scenario, Covered</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Dinner</h3>
              <p>Eating alone tonight? Solo mode helps you break out of your comfort zone and discover new spots in under 2 minutes.</p>
              <Link to="/what-to-eat-tonight" className="use-case-link">What to Eat Tonight →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Couples</h3>
              <p>The #1 use case. Both partners swipe and match on mutual favorites. No more "I don't care, you pick."</p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Couples Picker →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👨‍👩‍👧‍👦</span>
              <h3>Family Dinner</h3>
              <p>Get the whole family involved. Everyone swipes, even the kids. Find a restaurant the whole crew agrees on.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🌙</span>
              <h3>Date Night</h3>
              <p>Make restaurant picking part of the fun. Both swipe, reveal your matches, and head to your mutual top pick.</p>
              <Link to="/date-night-restaurants" className="use-case-link">Date Night →</Link>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Better Than Every Alternative</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ "What Do You Want?"</h3>
              <p>The question that starts every dinner argument. Leads to 30 minutes of vetoes, sighs, and settling for delivery from the same place.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Google Maps / Yelp</h3>
              <p>50 pins on a map. Hundreds of reviews. No group voting. You spend your evening reading reviews instead of eating dinner.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Random Wheel Spinners</h3>
              <p>Fun novelty, terrible results. Pure randomness ignores everyone's preferences. You might get fast food when you wanted a nice dinner out.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily</h3>
              <p>Smart matching with real restaurant data. Preference filters. Couple & group voting. Decision in 2 minutes. The dinner decision app that works.</p>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is the best dinner decision app?</h3>
              <p>Hangrily is the #1 dinner decision app with Tinder-style swiping, real restaurant data, and solo/couple/group modes. Most sessions take under 2 minutes.</p>
            </div>
            <div className="faq-item">
              <h3>Is there an app that decides dinner for you?</h3>
              <p>Yes! Hangrily shows restaurant cards one at a time. You swipe to vote. After 3 rounds, it picks the winner. For couples and groups, it finds mutual favorites.</p>
            </div>
            <div className="faq-item">
              <h3>How do couples decide dinner?</h3>
              <p>Couple Mode — both swipe independently, match on mutual likes. Eliminates the veto cycle completely.</p>
            </div>
            <div className="faq-item">
              <h3>Is Hangrily free?</h3>
              <p>Try free with no account. 3 sessions/week for guests. Premium is $9.99/month for unlimited access.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Dinner Decided in 2 Minutes</h2>
          <p>Join thousands who've ended nightly dinner arguments. Try free — no account needed.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Decide Dinner Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
